import { ApolloProvider } from '@apollo/client';
import { enableMapSet } from 'immer';
import PropTypes from 'prop-types';
import { memo, Suspense } from 'react';

import Routes from '../Routes';
import SplitProvider from '../SplitProvider';

import clientApollo from '~/api/clientApollo';
import asyncComponent from '~/components/AsyncComponent';
import { ConfirmDialog } from '~/components/ConfirmDialog';
import { ConfirmDialogProvider } from '~/components/ConfirmDialogV2/contexts/confirmDialogProvider';
import { HeaderProvider } from '~/components/HeaderProvider';
import Loading from '~/components/Loading';
import Snackbar from '~/components/Snackbar';
import ThemeProvider from '~/components/ThemeProvider';
import Toast from '~/components/Toast';
import aiChatPanelConfig from '~/scenes/AI/components/AIChat/AIChat';
import GlobalSidePanel from '~/scenes/AI/components/GlobalSidePanel/GlobalSidePanel';
import Eula from '~/scenes/Governance/scenes/Eula';
import PipelineAccessDenied from '~/scenes/Governance/scenes/PipelineAccessDenied';
import RealmAccessDenied from '~/scenes/Governance/scenes/RealmAccessDenied';
import { useSyncReduxToZustand } from '~/scenes/Governance/stores/useSyncReduxToZustand';

const Profile = asyncComponent(
  () => import('~/scenes/Governance/scenes/Profile')
);
const NewPassword = asyncComponent(
  () => import('~/scenes/Governance/scenes/NewPassword')
);
const TwoFactor = asyncComponent(
  () => import('~/scenes/Governance/scenes/TwoFactor')
);
const FallBackChat = asyncComponent(
  () => import('~/scenes/Governance/scenes/FallBackChat')
);

const SupportAccess = asyncComponent(
  () => import('~/scenes/Governance/scenes/SupportAccess')
);

const globalSidePanels = [aiChatPanelConfig];

const Application = ({ loading, userData, hiddenPages }) => {
  enableMapSet();
  useSyncReduxToZustand();

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <ApolloProvider client={clientApollo}>
          <SplitProvider>
            <ThemeProvider>
              <>
                <Snackbar />
                <ConfirmDialog />
                <ConfirmDialogProvider>
                  <Eula />
                </ConfirmDialogProvider>
                <Toast />
                <FallBackChat />
                {userData && (
                  <>
                    <PipelineAccessDenied />
                    <RealmAccessDenied />
                    <NewPassword />
                    <TwoFactor />
                    <Profile />
                    <SupportAccess />
                  </>
                )}
                <HeaderProvider>
                  <GlobalSidePanel panels={globalSidePanels}>
                    <Suspense fallback={<Loading />}>
                      <Routes hiddenPages={hiddenPages} />
                    </Suspense>
                  </GlobalSidePanel>
                </HeaderProvider>
              </>
            </ThemeProvider>
          </SplitProvider>
        </ApolloProvider>
      )}
    </>
  );
};

Application.propTypes = {
  loading: PropTypes.bool,
  userData: PropTypes.object,
  hiddenPages: PropTypes.array
};

export default memo(Application);
